import { ReactNode, useContext } from 'react';
import { SmartContext } from '../../../../library/Core/SmartContext';
import { getDomainValueForCode } from '../../../../library/Core/SmartFunctions';

const imageBasePath = process.env.REACT_APP_IMAGE_BASEPATH;
const fallbackProfileImage = `${imageBasePath}/dummy_male.svg`;
const verifiedBadge = `${imageBasePath}/blue-tick.svg`;
const businessIcon = `${imageBasePath}/blue-building.svg`;
const locationIcon = `${imageBasePath}/blue-map-ponter.svg`;
const categoryIcon = `${imageBasePath}/blue-brifecase.svg`;

export interface MarketplaceProvider {
    id: number | string;
    userName: string;
    businessName: string;
    location: string;
    category: string;
    experienceYears: number;
    profilePicture: string;
    degree?: string;
    year?: string | number;
}

interface ProviderCardProps {
    provider: MarketplaceProvider;
    actionLabel?: string;
    onViewDetails?: (provider: MarketplaceProvider) => void;
    onSendEnquiry?: (provider: MarketplaceProvider) => void;
    viewDetailsTrigger?: ReactNode;
    sendEnquiryTrigger?: ReactNode;
    profileImageOverride?: string;
    fallbackProfileImageOverride?: string;
}

const ProviderCard = ({
    provider,
    actionLabel = 'View Details',
    onViewDetails,
    onSendEnquiry,
    viewDetailsTrigger,
    sendEnquiryTrigger,
    profileImageOverride,
    fallbackProfileImageOverride
}: ProviderCardProps) => {
    const { state } = useContext(SmartContext);
    const handleImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
        event.currentTarget.onerror = null;
        event.currentTarget.src = fallbackProfileImageOverride || fallbackProfileImage;
    };

    const locationLabel = getDomainValueForCode(provider.location, 'COUNTRY_CODE', state) || provider.location || 'Location not available';

    return (
        <div className="card rounded-3 overflow-hidden h-100 border-0 shadow-sm">
            <div className="d-flex align-items-center p-3 text-white rounded-top bg-darker-blue">
                <div className="position-relative me-3">
                    <img
                        src={profileImageOverride || provider.profilePicture || fallbackProfileImageOverride || fallbackProfileImage}
                        className="rounded-circle border border-2 border-white object-fit-cover"
                        width="70"
                        height="70"
                        alt={provider.userName || 'Provider'}
                        onError={handleImageError}
                    />
                    <i className="position-absolute top-0 end-0">
                        <img src={verifiedBadge} width="16" alt="Verified" />
                    </i>
                </div>
                <div className="min-width-0">
                    <h6 className="mb-0 fw-semibold text-truncate" title={provider.userName}>{provider.userName || 'Alumni Provider'}</h6>
                    <small className="d-block text-truncate" title={[provider.degree, provider.year].filter(Boolean).join(', ')}>
                        {[provider.degree, provider.year].filter(Boolean).join(', ') || 'Alumni Marketplace'}
                    </small>
                </div>
            </div>

            <div className="card-body">
                <div className="d-flex align-items-start mb-2">
                    <i className="me-2 flex-shrink-0"><img src={businessIcon} width="20" alt="Business" /></i>
                    <small className="mt-0 font-14 font-400 text-truncate" title={provider.businessName}>{provider.businessName || 'Business name not available'}</small>
                </div>
                <div className="d-flex align-items-start mb-2">
                    <i className="me-2 flex-shrink-0"><img src={locationIcon} width="20" alt="Location" /></i>
                    <small className="mt-0 font-14 font-400 text-truncate" title={locationLabel}>{locationLabel}</small>
                </div>
                <div className="d-flex align-items-start">
                    <i className="me-2 flex-shrink-0"><img src={categoryIcon} width="20" alt="Category" /></i>
                    <small className="mt-0 font-14 font-400 text-truncate" title={provider.category}>{provider.category || 'Category not available'}</small>
                </div>
            </div>

            <div className="d-flex align-items-center justify-content-between gap-2 px-3 py-3 lite-blue flex-wrap">
                <div className="font-14 font-500">Exp. {provider.experienceYears || 0} Years</div>
                <div className="d-flex gap-2">
                    {viewDetailsTrigger || (
                        <button
                            type="button"
                            className="btn-skyblue btn-sm rounded-pill px-3 font-14 font-400"
                            onClick={() => onViewDetails?.(provider)}>
                            {actionLabel}
                        </button>
                    )}
                    {sendEnquiryTrigger || (
                        <button
                            type="button"
                            className="btn btn-send py-0 ps-2 pe-2"
                            aria-label={`Send enquiry to ${provider.userName || 'provider'}`}
                            onClick={() => onSendEnquiry?.(provider)}>
                            <i className="icon-send me-1"></i>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProviderCard;